import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Users, Stethoscope, Receipt, ShieldCheck, Activity, Building2, Layers } from "lucide-react";
import RenewBanner from "@/components/RenewBanner";

const KPI_TONES = {
  brand:   { bg: "bg-gradient-to-br from-brand to-brand-700",       text: "text-white",       icon: "text-white/90",        sub: "text-white/70" },
  sky:     { bg: "bg-gradient-to-br from-sky-500 to-sky-700",       text: "text-white",       icon: "text-white/90",        sub: "text-white/70" },
  teal:    { bg: "bg-gradient-to-br from-teal-500 to-teal-700",     text: "text-white",       icon: "text-white/90",        sub: "text-white/70" },
  emerald: { bg: "bg-gradient-to-br from-emerald-500 to-emerald-700", text: "text-white",     icon: "text-white/90",        sub: "text-white/70" },
  amber:   { bg: "bg-gradient-to-br from-amber-500 to-amber-700",   text: "text-white",       icon: "text-white/90",        sub: "text-white/70" },
  violet:  { bg: "bg-gradient-to-br from-violet-500 to-violet-700", text: "text-white",       icon: "text-white/90",        sub: "text-white/70" },
  rose:    { bg: "bg-gradient-to-br from-rose-500 to-rose-700",     text: "text-white",       icon: "text-white/90",        sub: "text-white/70" },
  indigo:  { bg: "bg-gradient-to-br from-indigo-500 to-indigo-700", text: "text-white",       icon: "text-white/90",        sub: "text-white/70" },
};

function Kpi({ icon: Icon, label, value, hint, testid, tone = "brand" }) {
  const t = KPI_TONES[tone] || KPI_TONES.brand;
  return (
    <div className={`kpi-card ${t.bg} ${t.text} shadow-lg border-0`} data-testid={testid}>
      <div className="mb-2 flex items-center justify-between">
        <div className={`text-xs font-semibold uppercase tracking-wider ${t.sub}`}>{label}</div>
        <Icon size={18} className={t.icon} strokeWidth={2} />
      </div>
      <div className="font-heading text-3xl font-semibold">{value}</div>
      {hint && <div className={`mt-1 text-[11px] ${t.sub}`}>{hint}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { user, branch } = useAuth();
  const [data, setData] = useState(null);
  const [saStats, setSaStats] = useState(null);
  // For hospital_admin / branch_admin / it_admin: choose consolidated or session-branch view.
  // Default to consolidated to preserve historical behaviour.
  const canToggleScope = ["hospital_admin", "it_admin"].includes(user.role);
  const [scope, setScope] = useState(canToggleScope ? "all" : "current"); // 'all' | 'current'

  useEffect(() => {
    if (user.role === "super_admin") {
      api.get("/superadmin/dashboard").then(({ data }) => setSaStats(data));
      return;
    }
    setData(null);
    const params = new URLSearchParams();
    if (scope === "all" && canToggleScope) params.set("all_branches", "true");
    else if (scope === "current" && branch?.id) params.set("branch_id", branch.id);
    const qs = params.toString();
    api.get(`/reports/dashboard${qs ? `?${qs}` : ""}`).then(({ data }) => setData(data)).catch(() => {});
  }, [user.role, scope, branch?.id, canToggleScope]);

  if (user.role === "super_admin") {
    return (
      <div className="space-y-6 animate-fade-in">
        <div>
          <div className="section-label">SaaS Console</div>
          <h1 className="font-heading text-3xl font-semibold">Platform overview</h1>
        </div>
        {saStats ? (
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Kpi icon={Building2} label="Hospitals" value={saStats.hospitals} hint={`${saStats.active_hospitals} active`} testid="kpi-hospitals" tone="brand" />
            <Kpi icon={Building2} label="Branches" value={saStats.branches} testid="kpi-branches" tone="teal" />
            <Kpi icon={Users} label="Users" value={saStats.users} testid="kpi-users" tone="indigo" />
            <Kpi icon={Users} label="Patients" value={saStats.patients} testid="kpi-sa-patients" tone="emerald" />
          </div>
        ) : <div className="text-sm text-ink-muted">Loading…</div>}
      </div>
    );
  }

  if (!data) return <div className="text-sm text-ink-muted">Loading dashboard…</div>;
  const k = data.kpis;

  return (
    <div className="space-y-6 animate-fade-in">
      <RenewBanner userRole={user.role} />
      <div className="flex items-center justify-between">
        <div>
          <div className="section-label">Today at a glance</div>
          <h1 className="font-heading text-3xl font-semibold">Clinical dashboard</h1>
          <div className="mt-1 text-xs text-ink-muted">
            Showing data for{" "}
            <b>{scope === "all" && canToggleScope ? "all branches of this hospital" : (branch?.name || "your branch")}</b>
          </div>
        </div>

        {canToggleScope && (
          <div className="inline-flex overflow-hidden rounded-full border border-border bg-white text-xs shadow-sm" data-testid="dashboard-scope-toggle">
            <button
              type="button"
              data-testid="dashboard-scope-all"
              onClick={() => setScope("all")}
              className={`flex items-center gap-1.5 px-3 py-1.5 transition ${scope === "all" ? "bg-brand text-white" : "text-ink hover:bg-secondary"}`}
            >
              <Layers size={12} /> All branches
            </button>
            <button
              type="button"
              data-testid="dashboard-scope-current"
              onClick={() => setScope("current")}
              disabled={!branch?.id}
              className={`flex items-center gap-1.5 px-3 py-1.5 transition ${scope === "current" ? "bg-brand text-white" : "text-ink hover:bg-secondary disabled:opacity-40"}`}
            >
              <Building2 size={12} /> {branch?.code || "Current"}
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-6">
        <Kpi icon={Users} label="Patients" value={k.patients} hint="cumulative" testid="kpi-patients" tone="sky" />
        <Kpi icon={Stethoscope} label="OPD today" value={k.visits_today} testid="kpi-opd-today" tone="teal" />
        <Kpi icon={Activity} label="Consultations" value={k.consultations_today} hint="today" testid="kpi-consultations" tone="violet" />
        <Kpi icon={Receipt} label="Net collections" value={`₹ ${k.collections_today.toLocaleString("en-IN")}`} hint="today (post-refund)" testid="kpi-collections" tone="emerald" />
        <Kpi icon={Receipt} label="Refunds" value={`₹ ${(k.refunds_today || 0).toLocaleString("en-IN")}`} hint="today" testid="kpi-refunds" tone="rose" />
        <Kpi icon={ShieldCheck} label="ABDM linked" value={k.abdm_linked} hint="ABHA patients" testid="kpi-abdm" tone="amber" />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card data-testid="chart-opd-trend" className="border-0 shadow-md bg-gradient-to-br from-sky-50 to-white">
          <CardHeader>
            <CardTitle className="font-heading text-base text-sky-900 flex items-center gap-2">
              <Stethoscope size={16} className="text-sky-600" />OPD visits — last 7 days
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.opd_trend}>
                <defs>
                  <linearGradient id="opdGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0EA5E9" stopOpacity={1} />
                    <stop offset="100%" stopColor="#0369A1" stopOpacity={0.8} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#475569" }} />
                <YAxis tick={{ fontSize: 11, fill: "#475569" }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="count" fill="url(#opdGradient)" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card data-testid="chart-revenue-trend" className="border-0 shadow-md bg-gradient-to-br from-emerald-50 to-white">
          <CardHeader>
            <CardTitle className="font-heading text-base text-emerald-900 flex items-center gap-2">
              <Receipt size={16} className="text-emerald-600" />Collections — last 7 days (₹)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={data.revenue_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#475569" }} />
                <YAxis tick={{ fontSize: 11, fill: "#475569" }} />
                <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                <Line type="monotone" dataKey="revenue" stroke="#059669" strokeWidth={3} dot={{ r: 4, fill: "#10B981", strokeWidth: 2, stroke: "#FFFFFF" }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
